import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../types/todoNote';

/**
 * 보드별(boardId) 본문 스냅샷 LS 캐시. `revision`이 목록(`listTodoShareBoards`)의 값과 같으면
 * `loadTodoShareAsOwner` 풀 조회 없이 재사용한다.
 */
export interface TodoShareBoardContentSnapshot {
  title: string;
  revision: number;
  public_token?: string | null;
  is_active?: boolean | null;
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  postit_layout?: Record<string, { x: number; y: number }>;
}

const CACHE_VERSION = 'v1';

function storageKey(boardId: string): string {
  return `cp.todoShareBoardContent.${CACHE_VERSION}.${boardId.trim()}`;
}

export function loadTodoShareBoardContentCache(boardId: string): TodoShareBoardContentSnapshot | null {
  const bid = boardId.trim();
  if (!bid || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(bid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TodoShareBoardContentSnapshot> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.revision !== 'number' || !Array.isArray(parsed.categories) || !Array.isArray(parsed.items)) {
      return null;
    }
    return {
      title: typeof parsed.title === 'string' ? parsed.title : '',
      revision: parsed.revision,
      public_token: typeof parsed.public_token === 'string' ? parsed.public_token : null,
      is_active: typeof parsed.is_active === 'boolean' ? parsed.is_active : null,
      categories: parsed.categories as TodoNoteCategoryDraft[],
      items: parsed.items as TodoNoteItemDraft[],
      postit_layout:
        parsed.postit_layout && typeof parsed.postit_layout === 'object'
          ? (parsed.postit_layout as Record<string, { x: number; y: number }>)
          : undefined,
    };
  } catch {
    return null;
  }
}

export function saveTodoShareBoardContentCache(
  boardId: string,
  snapshot: TodoShareBoardContentSnapshot
): void {
  const bid = boardId.trim();
  if (!bid || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(bid), JSON.stringify(snapshot));
  } catch {
    /* quota 등은 무시 */
  }
}

export function invalidateTodoShareBoardContentCache(boardId: string): void {
  const bid = boardId.trim();
  if (!bid || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(bid));
  } catch {
    /* ignore */
  }
}

/** 대시보드 저장 후 노트 페이지가 revision 히트·즐겨찾기를 같이 쓰도록 본문만 패치한다. */
export function patchTodoShareBoardContentSnapshot(
  boardId: string,
  patch: Pick<TodoShareBoardContentSnapshot, 'revision' | 'categories' | 'items'>
): void {
  const cur = loadTodoShareBoardContentCache(boardId);
  saveTodoShareBoardContentCache(boardId, {
    title: cur?.title ?? '',
    revision: patch.revision,
    public_token: cur?.public_token ?? null,
    is_active: cur?.is_active ?? null,
    categories: patch.categories,
    items: patch.items,
    postit_layout: cur?.postit_layout,
  });
}

/** 본문 revision은 유지한 채 포스트잇 좌표만 캐시에 반영한다. */
export function patchTodoShareBoardContentPostitLayout(
  boardId: string,
  layout: Record<string, { x: number; y: number }>
): void {
  const cur = loadTodoShareBoardContentCache(boardId);
  if (!cur) return;
  saveTodoShareBoardContentCache(boardId, { ...cur, postit_layout: layout });
}
